/** @file Checks manifest.json's shape before a loader fetches the files it names; throws BUNDLE_FORMAT. */
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { BundleManifest } from '@zilo/pose3d-export/types/bundle';

type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const isSize = (v: unknown): boolean => Array.isArray(v) && v.length === 2 && v.every((n) => Number.isInteger(n) && (n as number) > 0);

function fail(path: string, expected: string): never {
  return throwError('BUNDLE_FORMAT', { expected }, { path: path || '/', hint: `expected ${expected}` });
}

function record(m: Obj, key: string, item: (v: unknown) => boolean, expected: string): void {
  const map = m[key];
  if (!isObj(map)) fail(`/${key}`, 'an object');
  for (const [k, v] of Object.entries(map)) if (!item(v)) fail(`/${key}/${k}`, expected);
}

const FIELDS: [string, (v: unknown) => boolean, string][] = [
  ['format', (v) => v === 'pose3d/bundle@1', "'pose3d/bundle@1'"],
  ['id', (v) => typeof v === 'string', 'a string'],
  ['name', (v) => typeof v === 'string', 'a string'],
  ['revision', (v) => Number.isInteger(v), 'an integer'],
  ['createdAt', (v) => typeof v === 'number' && Number.isFinite(v), 'a number'],
  ['puppet', (v) => typeof v === 'string', 'a path'],
  ['profile', isObj, 'an object'],
  ['hashAlgo', (v) => v === 'fnv1a64' || v === 'sha256', "'fnv1a64' or 'sha256'"],
];

/**
 * `manifest` typed when its shape is a `BundleManifest` (format `pose3d/bundle@1`, string paths, `clips`, `sheets`
 * and `files` maps); otherwise throws `BUNDLE_FORMAT` at the first wrong field (paths relative to the manifest).
 */
export function checkManifest(manifest: unknown): BundleManifest {
  if (!isObj(manifest)) return fail('', 'an object');
  for (const [key, ok, expected] of FIELDS) if (!ok(manifest[key])) fail(`/${key}`, expected);
  record(manifest, 'clips', (v) => typeof v === 'string', 'a path');
  record(manifest, 'sheets', (v) => isObj(v) && typeof v.file === 'string' && isSize(v.size), '{ file, size }');
  record(manifest, 'files', (v) => isObj(v) && Number.isInteger(v.bytes) && typeof v.hash === 'string', '{ bytes, hash }');
  return manifest as BundleManifest;
}
